import { forwardRef, useImperativeHandle, useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';
import './SecurityStep.scss';
import showPasswordIcon from '@/assets/icons/show-password-icon.svg';

export interface SecurityFormData {
  username: string;
  password: string;
  confirmPassword: string;
  securityQuestion: string;
  securityAnswer: string;
  agreeToTerms: boolean;
}

export interface SecurityStepRef {
  submitForm: () => Promise<boolean>;
  isValid: () => boolean;
  getFormData: () => SecurityFormData;
}

interface SecurityStepProps {
  initialData?: Partial<SecurityFormData>;
  onTermsChange?: (accepted: boolean) => void;
}

export const SecurityStep = forwardRef<SecurityStepRef, SecurityStepProps>((props, ref) => {
  const { t } = useTranslation('auth');
  const { register, handleSubmit, formState: { errors }, getValues, watch } = useForm<SecurityFormData>({
    defaultValues: props.initialData,
    mode: 'onChange'
  });
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const password = watch('password');
  const agreeToTerms = watch('agreeToTerms');

  // Notify parent when terms checkbox changes
  useEffect(() => {
    if (props.onTermsChange) {
      props.onTermsChange(!!agreeToTerms);
    }
  }, [agreeToTerms, props]);

  // Expose methods to parent component
  useImperativeHandle(ref, () => ({
    submitForm: async () => {
      let isValid = false;
      await handleSubmit(
        () => {
          isValid = true;
        },
        () => {
          isValid = false;
        }
      )();
      return isValid;
    },
    isValid: () => {
      return Object.keys(errors).length === 0;
    },
    getFormData: () => {
      return getValues();
    }
  }));

  return (
    <div className="security-step">
      <div className="security-step__header">
        <h2 className="security-step__title">{t('registration.security.title')}</h2>
        <p className="security-step__description">{t('registration.security.description')}</p>
      </div>

      <form onSubmit={(e) => e.preventDefault()} className="security-step__form">
        <div className="form-row">
          <div className="form-field">
            <label className="form-field__label" htmlFor="username">
              {t('registration.security.username')}*
            </label>
            <small className="form-field__hint">
              {t('registration.security.usernameHint')}
            </small>
            <input
              type="text"
              id="username"
              className="form-field__input"
              autoComplete="username"
              spellCheck="false"
              {...register('username', {
                required: true,
                minLength: 4,
                maxLength: 20,
                pattern: {
                  value: /^[^@\s]*$/,
                  message: t('registration.error.usernameNoSpaces')
                },
                validate: (value) => {
                  if (value.includes(' ')) {
                    return t('registration.error.usernameNoSpaces');
                  }
                  if (value.includes('@')) {
                    return 'Username must not contain @';
                  }
                  return true;
                }
              })}
            />
            {errors.username && (
              <span className="form-field__error">
                {errors.username.type === 'required' && t('registration.error.required')}
                {errors.username.type === 'minLength' && t('registration.error.usernameMinLength')}
                {errors.username.type === 'maxLength' && 'Username must not exceed 20 characters'}
                {errors.username.type === 'pattern' && errors.username.message}
                {errors.username.type === 'validate' && errors.username.message}
              </span>
            )}

          </div>
        </div>

        <div className="form-row">
          <div className="form-field">
            <label className="form-field__label" htmlFor="password">
              {t('registration.security.password')}*
            </label>
            <small className="form-field__hint">
              {t('registration.security.requirements')}
            </small>
            <div className="form-field__password">
              <input
                type={showPassword ? 'text' : 'password'}
                id="password"
                className="form-field__input"
                autoComplete="new-password"
                {...register('password', {
                  required: true,
                  minLength: 8,
                  pattern: /^(?=.*[A-Z])(?=.*[a-z])(?=.*[0-9])(?=.*[!@#$%^&*])/
                })}
              />
              <button
                type="button"
                className="password-toggle"
                onClick={() => setShowPassword(!showPassword)}
                aria-label={showPassword ? 'Hide password' : 'Show password'}
              >
                <img src={showPasswordIcon} alt="Toggle password visibility" />
              </button>
            </div>
            {errors.password && (
              <span className="form-field__error">
                {errors.password.type === 'required' && t('registration.error.required')}
                {errors.password.type === 'minLength' && 'Password must be at least 8 characters'}
                {errors.password.type === 'pattern' && t('registration.error.password')}
              </span>
            )}

          </div>

          <div className="form-field">
            <label className="form-field__label" htmlFor="confirmPassword">
              {t('registration.security.confirmPassword')}*
            </label>
            <small className="form-field__hint">
              {t('registration.security.requirements')}
            </small>
            <div className="form-field__password">
              <input
                type={showConfirmPassword ? 'text' : 'password'}
                id="confirmPassword"
                className="form-field__input"
                autoComplete="new-password"
                {...register('confirmPassword', {
                  required: true,
                  validate: value => value === password || t('registration.error.passwordMatch')
                })}
              />
              <button
                type="button"
                className="password-toggle"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                aria-label={showConfirmPassword ? 'Hide password' : 'Show password'}
              >
                <img src={showPasswordIcon} alt="Toggle password visibility" />
              </button>
            </div>
            {errors.confirmPassword && (
              <span className="form-field__error">
                {errors.confirmPassword.type === 'required' && t('registration.error.required')}
                {errors.confirmPassword.type === 'validate' && t('registration.error.passwordMatch')}
              </span>
            )}
          </div>
        </div>



        <div className="form-row">
          <div className="form-field checkbox-field">
            <input
              type="checkbox"
              id="agreeToTerms"
              className="form-field__checkbox"
              {...register('agreeToTerms', { required: true })}
            />
            <label htmlFor="agreeToTerms" className="form-field__checkbox-label">
              {t('registration.security.termsAgreement')}
            </label>
          </div>
        </div>

        {errors.agreeToTerms && (
          <span className="form-field__error">
            {t('registration.error.termsRequired')}
          </span>
        )}
      </form>
    </div>
  );
});
